import { useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { startLinkedInLogin } from '@/modules/auth/linkedinSso';
import {
  authButtonClickedEvents,
  type AuthIntent,
} from '@/modules/auth/types/externalLogin';
import { Button } from '@/modules/ui/components/button/Button';

import { LinkedInLogo } from './LinkedInLogo';

interface LinkedInLoginProps {
  returnTo?: string;
  /** True once the Terms Notice beside the button shows its link. */
  acceptTerms: boolean;
  /** Whether the callback reports `userSignedUp` or `userSignedIn`. */
  intent: AuthIntent;
  onError: (error?: Error) => void;
}

export const LinkedInLogin = ({
  returnTo,
  acceptTerms,
  intent,
  onError,
}: LinkedInLoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const trackEvent = useTrackEvent();

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      trackEvent(authButtonClickedEvents[intent], { authMethod: 'LinkedIn' });
      trackEvent('linkedInSSO', {
        stage: 'started',
      });

      await startLinkedInLogin(returnTo, acceptTerms, intent);
    } catch (error) {
      setIsLoading(false);
      onError(error instanceof Error ? error : undefined);
    }
  };

  return (
    <Button
      variant="outline"
      loading={isLoading}
      onClick={handleLogin}
      // Matches Google's large button beside it: 40px tall, 4px corners, the
      // icon at the left edge and the label centred.
      className="relative h-10 w-full rounded-[4px] font-medium active:scale-[0.98]"
    >
      {!isLoading && (
        <LinkedInLogo className="absolute left-3 size-4 text-[#0A66C2]" />
      )}
      {intent === 'signUp' ? 'Sign up with LinkedIn' : 'Continue with LinkedIn'}
    </Button>
  );
};
